import axios, { type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/authStore";
import { getUseToast } from "../../plugins/useToast";
import type { Router } from "vue-router";
import { keysToCamel } from "@/js/helpers/object.helper";

interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
   sanoLanguage?: string;
}

// Set global defaults for axios. These apply everywhere axios is imported.
axios.defaults.withCredentials = true;

// https://axios-http.com/docs/interceptors
function setupAxiosInterceptors(router: Router, i18n: any) {
   const authStore = useAuthStore();

   // Request interceptor
   axios.interceptors.request.use(
      async (request: CustomInternalAxiosRequestConfig) => {
         // for all requests, set 'Sano-Language' in the headers
         request.headers["Sano-Language"] = i18n.locale.value;

         if (request.url[0] === "/") {
            request.headers["Client"] = "participant";

            let fullApiPath = "";
            // pre-rendering OR local development (requires full path because the server port is different)
            if (import.meta.env.SSR || import.meta.env.MODE !== "production") {
               fullApiPath = `${import.meta.env.VITE_APP_API_BASE_URL}${import.meta.env.VITE_APP_API_PATH}`;
            }
            // production (API endpoint is the same as the base URL)
            else {
               fullApiPath = `${import.meta.env.VITE_APP_API_PATH}`;
            }

            // set the API path based on the environment
            request.url = `${fullApiPath}${request.url}`;

            // Note: if tokenStatus is null, the user is likely logged out. Proceed with the request without an auth header.
            // If the user tries to access a resource without authorization, a 401 will be returned, and this is handled in response interceptor
            let tokenStatus = authStore.getTokenStatus();

            // If this request is NOT a refresh request, determine if a refresh is needed
            if (!request.url.includes("/x/token-refresh")) {
               if (authStore.getIsRefreshingToken()) {
                  // getStatusAfterRefreshCompletion will wait until the ongoing refresh request is complete
                  // or has failed to complete in the max time allowed, and then return updated token status
                  tokenStatus = await authStore.getStatusAfterRefreshCompletion();
               } else if (tokenStatus === "expired") {
                  await authStore.refreshTheToken();
                  tokenStatus = authStore.getTokenStatus();
               }
            }

            // If the token was already valid, or the refresh was succesful, add auth to the request
            if (tokenStatus === "valid") {
               const token = authStore.getToken();
               request.headers["Authorization"] = `Bearer ${token}`;
            }

            request.headers["Timestamp"] = Date.now();
         }
         return request;
      },
      (error) => {
         return Promise.reject(error);
      }
   );

   // Response interceptor
   axios.interceptors.response.use(
      (response) => {
         if (!import.meta.env.SSR && response.headers?.token) {
            // update jwt if a new one is sent back - saves having to set the token on: login, registration, reset password, etc
            // (only when not pre-rendering)
            authStore.setToken(response.headers.token);
         }

         // convert response data from snake_case to camelCase
         if (
            response.headers["content-type"] !== "application/pdf" &&
            !(response.config as any)?.ignoreCamelConversion
         ) {
            response.data = keysToCamel(response.data);
         }

         return response;
      },
      (error) => {
         const useToast = getUseToast(i18n.t);

         // 401 Unauthorized
         // Scenario: A user was logged in, but their session has expired, or a refresh request times out because of bad network connection
         if (error.response?.status === 401) {
            if (authStore.getTokenStatus() !== "valid") {
               useToast({ text: i18n.t("validation.refresh_error"), type: "error" });
            } else {
               useToast({ text: i18n.t("validation.error_401"), type: "error" });
            }

            authStore.destroyToken();

            // Re-route user when necessary (Not allowed / not found) but keep a handy redirect in there
            const queryPlusRedirect = router.currentRoute.value.query;
            queryPlusRedirect.redirect = router.currentRoute.value.path;

            router.push({ name: "login", query: queryPlusRedirect, params: { locale: i18n.locale.value } });
         }

         // reject the promise with the error object so that the error can be handled in the calling function
         return Promise.reject(error);
      }
   );
}

export { setupAxiosInterceptors };
